import { useState, useEffect, useCallback } from 'react';
import { listDesigns, renameDesign, deleteDesign, type DesignSummary } from '../lib/designStore';

interface DesignsPanelProps {
  onLoad: (id: string) => void;
  onClose: () => void;
}

export function DesignsPanel({ onLoad, onClose }: DesignsPanelProps) {
  const [designs, setDesigns] = useState<DesignSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setDesigns(await listDesigns());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load designs.');
      setDesigns([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleRename = async (id: string) => {
    setBusyId(id);
    try {
      await renameDesign(id, editName);
      setEditingId(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string, label: string) => {
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setBusyId(id);
    try {
      await deleteDesign(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <h3 className="modal-title">My Designs</h3>
        <p className="modal-subtitle">Saved designs load with their roof, panels, and production intact.</p>

        {error && <div className="form-error" style={{ marginBottom: 12 }}>{error}</div>}

        {designs === null && <div style={{ color: '#94a3b8', padding: '16px 0' }}>Loading…</div>}

        {designs !== null && designs.length === 0 && !error && (
          <div style={{ color: '#94a3b8', padding: '16px 0' }}>
            No saved designs yet. Create a design and click "Save Design".
          </div>
        )}

        <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          {designs?.map((d) => {
            const label = d.name || d.address;
            return (
              <div
                key={d.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid #334155',
                }}
              >
                {d.thumbnail ? (
                  <img
                    src={d.thumbnail}
                    alt=""
                    style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #334155', flexShrink: 0 }}
                  />
                ) : (
                  <div
                    style={{
                      width: 72,
                      height: 48,
                      borderRadius: 6,
                      border: '1px solid #334155',
                      background: '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#64748b',
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    ☀️
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  {editingId === d.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        className="address-input"
                        style={{ flex: 1, padding: '4px 8px', fontSize: 13 }}
                        value={editName}
                        autoFocus
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(d.id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <button
                        className="btn-design"
                        style={{ padding: '4px 10px', fontSize: 12 }}
                        disabled={busyId === d.id}
                        onClick={() => handleRename(d.id)}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.address} · {new Date(d.updated_at + 'Z').toLocaleDateString()}
                      </div>
                    </>
                  )}
                </div>

                {editingId !== d.id && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn-design"
                      style={{ padding: '6px 12px', fontSize: 12 }}
                      disabled={busyId === d.id}
                      onClick={() => onLoad(d.id)}
                    >
                      Load
                    </button>
                    <button
                      style={{
                        padding: '6px 10px',
                        fontSize: 12,
                        background: 'transparent',
                        border: '1px solid #334155',
                        borderRadius: 6,
                        color: '#94a3b8',
                        cursor: 'pointer',
                      }}
                      title="Rename"
                      aria-label={`Rename ${label}`}
                      disabled={busyId === d.id}
                      onClick={() => {
                        setEditingId(d.id);
                        setEditName(d.name || d.address);
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      style={{
                        padding: '6px 10px',
                        fontSize: 12,
                        background: 'transparent',
                        border: '1px solid #ef4444',
                        borderRadius: 6,
                        color: '#ef4444',
                        cursor: 'pointer',
                      }}
                      title="Delete"
                      aria-label={`Delete ${label}`}
                      disabled={busyId === d.id}
                      onClick={() => handleDelete(d.id, label)}
                    >
                      🗑
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
